import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DesignLayoutType, EventPageType } from '@graphql/generated-base';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { Box, ColumnsV2, Flex, styled } from '@withjoy/joykit';
import { Footer } from '../../components/Footer';
import { Body } from './components/Body';
import { brannanConfig, useBrannanDesign } from './LayoutBrannan.theme';
import { LazyWelcomeVideo } from '../../widgets/WelcomeVideo/WelcomeVideo.lazy';
import CoverPhoto from '../../components/CoverPhoto';
import { brannanFooterContainerStyles } from '@apps/guest/packages/layout-engine/components/Footer/Footer.styles';
import { useLayoutBrannanController } from './LayoutBrannan.controller';
import { findEventPageByPageSlug, getEventMenuPropsFromEvent } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { Shell } from './components/Shell';
import { isInIframe } from '@shared/utils/isInIframe';
import { withWindow } from '@shared/utils/withWindow';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import WebsiteHeader from './components/WebsiteHeader/WebsiteHeader';
import { useWebsiteHeaderSettings } from '../hooks/useWebsiteHeaderSettings/useWebsiteHeaderSettings';
import { useMasthead } from '../hooks/useMasthead/useMasthead';
import { isMastheadCompatibleLayout } from '@shared/utils/mastheadCompat';
import type { PagesRefs } from '../LayoutAloha/LayoutAloha.types';
import { useAlohaScroll } from '../LayoutAloha/hooks/useAlohaScroll';
import { CoverPhotoWrapper } from './components/CoverPhotoWrapper';
import type { LayoutBrannanProps } from './LayoutBrannan';
import { PreloadImages } from '../../components/PreloadPageImages/PreloadImages';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { useIsStaysRedesignActive } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Honeymoon/useIsStaysRedesignActive';
import { StandaloneWebsiteHeader } from './components/StandaloneWebsiteHeader/StandaloneWebsiteHeader';
import { useMultiPhotoMotionHeader } from './components/HeaderV2/components/HeaderMotion/multiPhotoMotionHeader';
import { EVENT_PAGE_SLUGS } from '@apps/guest/shared/constants/eventPageSlugs';
import { useAccommodationPageChecks } from '@apps/guest/hooks/useAccommodationPageChecks';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { useShouldRevealPageContent } from '@apps/guest/packages/layout-engine/components/PageContentVisibilityProvider';
import { useColorPaletteProvider } from '../ColorPaletteProvider/ColorPaletteProvider';
import { StickyNavBar } from '@apps/guest/packages/layout-engine/components/StickyNavBar';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useWelcomePagePhotoUrl } from '../hooks/useWelcomePagePhotoUrl';
import { isDeepLinkTargetSlug, useDeepLinkTargetSlug } from '../hooks/useDeepLinkTargetSlug';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useGuestSitePages } from '../../hooks/useGuestSitePages';
import { useHistory } from '@react-router';
import queryString from 'query-string';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import { getMigratedPageSlug, toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import { BrannanDesignProvider } from './BrannanDesignContext';
import { pxToRem } from '@withjoy/joykit/theme';
import FooterNav from '../../components/FooterNav/FooterNav';
import { SIMPLE_SCROLL_CONTAINER_ID } from '@apps/guest/routes/GuestSite/GuestSite.constants';
import { useQueryParamHelper } from '@shared/core/queryString';
import { MobileTopBarNav, MobileTopBarNavOffsetProvider } from '@apps/guest/packages/layout-engine/components/MobileTopBarNav';
import { BulkCustomPageDataProvider } from '@apps/guest/packages/layout-engine/widgets/Custom/BulkCustomPageDataProvider';

const { componentTitleBanner } = brannanConfig.applicatorHtmlProps;

const ScrollableContainer = styled(Box)<{ $anchoringConfined?: boolean }>`
  width: 100%;
  height: 100vh;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 1;

  /*
   * Make the deep-link target's 1px sentinel the ONLY scroll-anchor candidate in this scroller.
   * Per-section exclusions weren't enough here: unlike Aloha, sections aren't direct children of
   * the scroller — they sit inside Shell > Frames > Page > ColumnsV2 — so the in-flow
   * WebsiteHeader, the frame/Page boxes and the footer all stayed eligible, and Chrome held one of
   * THOSE while sections grew (the drift off-target plus the shift).
   *
   * overflow-anchor:none prunes an element AND its subtree, so the sentinel's ancestors must
   * stay eligible — hence excluding everything that is neither the sentinel nor one of its
   * ancestors. Anchor selection descends through those ancestors and can only terminate at the
   * sentinel. Where :has() is unsupported the rule is ignored and the per-section exclusions on
   * SectionWrapper still apply. PRNT-4799.
   *
   * Confined through load AND through the containment release, relaxed RELEASE_SETTLE_MS after
   * first input. Two constraints meet here: (1) once the guest scrolls, the sentinel can leave
   * the viewport and a confined scroller has NO legal anchor left — late height changes then
   * shift the page uncompensated (measured ~2,200px past the target); (2) but relaxing in the
   * same commit as the release lets Chrome re-anchor mid-swap to a node inside the rendering
   * sections and drift (measured +1,700 compensation against a +3,920 swap). The release always
   * happens with the sentinel on screen — a guest cannot have scrolled before their first
   * input — so confinement safely covers the swap, and the timer relaxes candidacy before the
   * guest can scroll anywhere it matters. Post-input shifts are CLS-exempt, so relaxing cannot
   * regress the load-time score. PRNT-4799.
   */
  ${({ $anchoringConfined }) =>
    $anchoringConfined
      ? `& *:not([data-scroll-anchor-sentinel]):not(:has([data-scroll-anchor-sentinel])) {
    overflow-anchor: none;
  }`
      : ''}
`;

// Moments and RSVP are from mono repo which will be accessible via navbar but not scrolling
/**
 * Section wrapper. Carries the scroll-anchoring rules in real CSS, NOT via joykit's `__css`:
 * `overflow-anchor` isn't in joykit's style-prop map, so a `__css={{ overflowAnchor }}` is
 * silently dropped and the rules never reach the document (verified: zero `overflow-anchor`
 * declarations in the SSR CSS while Aloha's styled-template version emitted five).
 *
 * Non-target sections opt out of anchor candidacy entirely. The target stays eligible but
 * excludes its own children, because its skeleton→content swap destroys whatever node Chrome
 * anchored on — Chrome then re-anchors deeper into the new content and tracks THAT, sliding the
 * viewport a full section. That leaves the never-mutating 1px sentinel as the only candidate, so
 * "anchored" can only mean "the target's top edge stays put". PRNT-4799.
 */
const SectionWrapper = styled(Box)<{ $isDeepLinkTarget?: boolean; $anchoringConfined?: boolean }>`
  ${({ $isDeepLinkTarget, $anchoringConfined }) =>
    !$anchoringConfined
      ? ''
      : $isDeepLinkTarget
        ? `& > *:not([data-scroll-anchor-sentinel]) {
    overflow-anchor: none;
  }`
        : 'overflow-anchor: none;'}
`;

// Pins a section above the deep-link target at its reservation while content loads. `hidden`, not
// `auto`: `auto` renders within ~half a viewport (i.e. the sections next to the target) and a
// rendered section ignores contain-intrinsic-size. PRNT-4799.
const DEFERRED_SECTION_STYLE: React.CSSProperties = { contentVisibility: 'hidden', containIntrinsicSize: '100vh' } as React.CSSProperties;

// Post-interaction: still contained, but renders on approach. `auto` keeps the real height once
// rendered. PRNT-4799.
const DEFERRED_SECTION_STYLE_INTERACTIVE: React.CSSProperties = { contentVisibility: 'auto', containIntrinsicSize: 'auto 100vh' } as React.CSSProperties;

// The release commit itself: `visible`, NOT `auto`. `auto` renders released sections lazily over
// ~2s — those trailing swaps fall outside the 500ms input-exemption window (measured: a countable
// 1.0 CLS on desktop) and compensation has to chase a moving target (measured ~160px residual
// drift). `visible` forces every deferred section to its real height in this ONE commit: the whole
// swap is compensated in a single frame and lands input-exempt. Swapped to the INTERACTIVE style
// two frames later so far-away sections resume being skipped at their now-known sizes. PRNT-4799.
const DEFERRED_SECTION_STYLE_RELEASING: React.CSSProperties = { contentVisibility: 'visible' } as React.CSSProperties;

// Not `scroll` — the programmatic landing scroll would release containment immediately. `focusin`
// covers assistive tech, whose browse-mode keys may never reach the page as `keydown` (`hidden`
// content is skipped by find-in-page and the a11y tree until released). PRNT-4799.
const USER_INPUT_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown', 'focusin'] as const;

const useHasUserInput = () => {
  const [hasUserInput, setHasUserInput] = useState(false);
  useEffect(() => {
    if (hasUserInput) return undefined;
    const release = (e: Event) => {
      // focusin counts only from inside the scroller. Body-level overlays autofocus themselves on
      // mount (#polaris-css-lockdown-container fires a trusted focusin ~100ms after landing), which
      // released containment with the guest's hands still off — racing the landing scroll. In-scroller
      // focus (Tab, assistive tech) still releases. PRNT-4799.
      if (e.type === 'focusin' && !(e.target instanceof Element && e.target.closest(`#${SIMPLE_SCROLL_CONTAINER_ID}`))) return;
      setHasUserInput(true);
    };
    return withWindow<(() => void) | undefined>(global => {
      USER_INPUT_EVENTS.forEach(eventName => global.addEventListener(eventName, release, { passive: true, capture: true }));
      return () => USER_INPUT_EVENTS.forEach(eventName => global.removeEventListener(eventName, release, { capture: true }));
    }, undefined);
  }, [hasUserInput]);
  return hasUserInput;
};

const PAGES_TO_BE_SKIPPED = [EventPageType.moments, EventPageType.rsvp];

// How long after first input the anchor-candidacy confinement stays on: released sections
// reach their real heights progressively (~2s observed), not in the release commit alone,
// and Chrome must keep anchoring on the sentinel through that window. PRNT-4799.
const RELEASE_SETTLE_MS = 3000;

type BodySectionProps = Readonly<{
  page: EventPageFragment;
  event: LayoutBrannanProps['event'];
  eventHandle: string;
  websiteHeader: ReturnType<typeof useWebsiteHeaderSettings>['websiteHeader'];
  isNativeAccommodations: boolean;
  isWelcomePage?: boolean;
  isCustomWelcomePage?: boolean;
  welcomePhotoUrl?: string | null;
  isStaysRedesignCurrentPage?: boolean;
  isRevealed?: boolean;
}>;

const BodySection = ({
  page,
  event,
  eventHandle,
  websiteHeader,
  isNativeAccommodations,
  isWelcomePage = false,
  isCustomWelcomePage = false,
  welcomePhotoUrl,
  isStaysRedesignCurrentPage,
  isRevealed,
}: BodySectionProps) => {
  const shouldHideColumn = useMemo(() => {
    return isNativeAccommodations && !page?.subTitle;
  }, [isNativeAccommodations, page?.subTitle]);

  return (
    <Body
      data-testid={`layout-body-${page.pageSlug}`}
      marginTop={[0, null, 9]}
      page={page}
      isStaysRedesignCurrentPage={isStaysRedesignCurrentPage}
      isRevealed={isRevealed}
      event={event}
      eventHandle={eventHandle}
      isSinglePage={true}
      isWelcomePage={isWelcomePage}
      isCustomWelcomePage={isCustomWelcomePage}
      photo={
        isWelcomePage && event.video ? (
          <ColumnsV2.Column
            style={page.type === EventPageType.custom ? { display: shouldHideColumn ? 'none' : 'block' } : {}}
            span={[12, 10]}
            offset={[0, 1]}
          >
            <LazyWelcomeVideo
              video={event.video}
              thumbnailUrl={event.video.thumbnailUrl || welcomePhotoUrl}
            />
          </ColumnsV2.Column>
        ) : isWelcomePage && websiteHeader ? null : (
          // Only hide page photo for native welcome page when websiteHeader is present
          // Custom welcome pages should show their cover photo
          <CoverPhotoWrapper
            renderFullColumn={page.photoPresentation?.presentationLayout !== 'center'}
            isCustomPage={page.type === EventPageType.custom}
            isNativeAccommodations={isNativeAccommodations}
            subTitle={page.subTitle}
          >
            <CoverPhoto
              websiteLayoutType={DesignLayoutType.brannan}
              photo={page.photoV2}
              pageId={page.id}
              pageType={page.type}
              pageSlug={page.pageSlug}
              photoPresentation={page.photoPresentation}
              // Single-page layout: every section cover sits below the 90vh hero at initial
              // load, so the sharp layer can defer until near-viewport (PRNT-5084). Multi-page
              // LayoutBrannan must NOT pass this — its cover is the current page's above-fold
              // body photo.
              lazyPagePhoto
            />
          </CoverPhotoWrapper>
        )
      }
    />
  );
};

interface PageSectionProps
  extends Readonly<{
    page: EventPageFragment;
    event: LayoutBrannanProps['event'];
    eventHandle: string;
    websiteHeader: ReturnType<typeof useWebsiteHeaderSettings>['websiteHeader'];
    pagesRef: Record<string, PagesRefs>;
    isWelcomePage: boolean;
    isCustomWelcomePage: boolean;
    welcomePhotoUrl?: string | null;
    /**
     * The section the guest landed on. It gets a viewport-filling floor so the section
     * below it starts past the fold — its widgets render client-side and arrive seconds
     * later, and that growth would otherwise displace the visible next section. A
     * permanent floor, so a short section can't let the next one drift back up. PRNT-4799.
     */
    isDeepLinkTarget?: boolean;
    /**
     * Section above the deep-link target: renders size-contained at its reservation, not at its live
     * height. Not `display: none` — it stays in the scroll flow and scrollable. PRNT-4799.
     */
    isDeferred?: boolean;
    /** Containment phase style for deferred sections — see DEFERRED_SECTION_STYLE* constants. */
    deferredStyle?: React.CSSProperties;
    /** Sentinel-only anchor candidacy still in force — see ScrollableContainer. */
    anchoringConfined?: boolean;
    /** Stays redesign is active and the URL names this page — see Body. */
    isStaysRedesignCurrentPage?: boolean;
    /** This section's chrome has been revealed — resolved by PagesSection, see Body. */
    isRevealed?: boolean;
  }> {}

const PageSectionAccommodationsPage = React.memo<PageSectionProps>(
  ({
    page,
    event,
    eventHandle,
    websiteHeader,
    pagesRef,
    welcomePhotoUrl,
    isDeepLinkTarget,
    isDeferred,
    deferredStyle,
    anchoringConfined,
    isStaysRedesignCurrentPage,
    isRevealed,
  }) => {
    const { isNativeAccommodationPage } = useAccommodationPageChecks(page.pageSlug);

    return (
      <SectionWrapper
        paddingTop={{ _: 0, sm2: pxToRem(96) }}
        paddingBottom={{ _: 10, sm2: 0 }}
        minHeight={isDeepLinkTarget ? '100vh' : undefined}
        $isDeepLinkTarget={isDeepLinkTarget}
        $anchoringConfined={anchoringConfined}
        style={isDeferred ? deferredStyle : undefined}
        key={page.id}
        ref={ref => (pagesRef[page.id] = { ref, page })}
      >
        {/* Direct child of SectionWrapper, as in the other branches: the target rule prunes every
            direct child that isn't the sentinel, and pruning takes the subtree with it — nested
            inside the Flex, the sentinel was ineligible and accommodations deep links lost anchoring
            entirely. PRNT-4799. */}
        {isDeepLinkTarget && (
          <div
            data-scroll-anchor-sentinel
            aria-hidden="true"
            // visibility: Brannan's frame hides its children with `visibility: hidden` while the style
            // applicator loads, and scroll anchoring cannot select an invisible node. Re-declaring it
            // here restores the sentinel as a candidate. 1px and empty. PRNT-4799.
            style={{ height: 1, marginBottom: -1, visibility: 'visible' }}
          />
        )}
        <Flex
          data-testid={'page-custom'}
          justifyContent="center"
          alignItems="center"
          margin={'0 auto'}
        >
          <BodySection
            page={page}
            event={event}
            eventHandle={eventHandle}
            websiteHeader={websiteHeader}
            isNativeAccommodations={isNativeAccommodationPage}
            isWelcomePage={false}
            welcomePhotoUrl={welcomePhotoUrl}
            isStaysRedesignCurrentPage={isStaysRedesignCurrentPage}
            isRevealed={isRevealed}
          />
        </Flex>
      </SectionWrapper>
    );
  },
);

const PageSectionCustomPage = React.memo<PageSectionProps>(
  ({
    page,
    event,
    eventHandle,
    websiteHeader,
    pagesRef,
    isWelcomePage,
    isCustomWelcomePage,
    welcomePhotoUrl,
    isDeepLinkTarget,
    isDeferred,
    deferredStyle,
    anchoringConfined,
    isStaysRedesignCurrentPage,
    isRevealed,
  }) => {
    const { isNativeAccommodationPage } = useAccommodationPageChecks(page.pageSlug);

    return (
      <SectionWrapper
        paddingTop={{ _: isWelcomePage ? 10 : 0, sm2: pxToRem(96) }}
        paddingBottom={{ _: 10, sm2: 0 }}
        minHeight={isDeepLinkTarget ? '100vh' : undefined}
        $isDeepLinkTarget={isDeepLinkTarget}
        $anchoringConfined={anchoringConfined}
        style={isDeferred ? deferredStyle : undefined}
        key={page.id}
        ref={ref => (pagesRef[page.id] = { ref, page })}
      >
        {isDeepLinkTarget && (
          <div
            data-scroll-anchor-sentinel
            aria-hidden="true"
            // visibility: Brannan's frame hides its children with `visibility: hidden` while the style
            // applicator loads, and scroll anchoring cannot select an invisible node — leaving NO legal
            // anchor for the whole load window, which is why single-page shifted where Aloha didn't.
            // visibility is overridable by descendants, so re-declaring it here restores the sentinel as
            // a candidate. 1px and empty, so nothing is visible. PRNT-4799.
            style={{ height: 1, marginBottom: -1, visibility: 'visible' }}
          />
        )}
        <BodySection
          page={page}
          event={event}
          eventHandle={eventHandle}
          websiteHeader={websiteHeader}
          isNativeAccommodations={isNativeAccommodationPage}
          isWelcomePage={isWelcomePage}
          isCustomWelcomePage={isCustomWelcomePage}
          welcomePhotoUrl={welcomePhotoUrl}
          isStaysRedesignCurrentPage={isStaysRedesignCurrentPage}
          isRevealed={isRevealed}
        />
      </SectionWrapper>
    );
  },
);

const PageSection = React.memo<PageSectionProps>(props => {
  const {
    page,
    event,
    eventHandle,
    websiteHeader,
    pagesRef,
    isWelcomePage,
    isCustomWelcomePage,
    welcomePhotoUrl,
    isDeepLinkTarget,
    isDeferred,
    deferredStyle,
    anchoringConfined,
    isStaysRedesignCurrentPage,
    isRevealed,
  } = props;
  const { isAccommodationsPage, isNativeAccommodationPage } = useAccommodationPageChecks(page.pageSlug);

  if (page.type === EventPageType.custom) {
    if (isAccommodationsPage) {
      return <PageSectionAccommodationsPage {...props} />;
    }
    return <PageSectionCustomPage {...props} />;
  }

  return (
    <SectionWrapper
      paddingTop={{ _: isWelcomePage ? 10 : 0, sm2: pxToRem(96) }}
      paddingBottom={{ _: 10, sm2: 0 }}
      minHeight={isDeepLinkTarget ? '100vh' : undefined}
      $isDeepLinkTarget={isDeepLinkTarget}
      $anchoringConfined={anchoringConfined}
      style={isDeferred ? deferredStyle : undefined}
      ref={ref => (pagesRef[page.id] = { ref, page })}
    >
      {isDeepLinkTarget && (
        <div
          data-scroll-anchor-sentinel
          aria-hidden="true"
          // visibility: Brannan's frame hides its children with `visibility: hidden` while the style
          // applicator loads, and scroll anchoring cannot select an invisible node — leaving NO legal
          // anchor for the whole load window, which is why single-page shifted where Aloha didn't.
          // visibility is overridable by descendants, so re-declaring it here restores the sentinel as
          // a candidate. 1px and empty, so nothing is visible. PRNT-4799.
          style={{ height: 1, marginBottom: -1, visibility: 'visible' }}
        />
      )}
      <BodySection
        page={page}
        event={event}
        eventHandle={eventHandle}
        websiteHeader={websiteHeader}
        isNativeAccommodations={isNativeAccommodationPage}
        isWelcomePage={isWelcomePage}
        isCustomWelcomePage={isCustomWelcomePage}
        welcomePhotoUrl={welcomePhotoUrl}
        isStaysRedesignCurrentPage={isStaysRedesignCurrentPage}
        isRevealed={isRevealed}
      />
    </SectionWrapper>
  );
});

const PagesSection = React.memo<{
  filteredPages: EventPageFragment[];
  event: LayoutBrannanProps['event'];
  eventHandle: string;
  websiteHeader: ReturnType<typeof useWebsiteHeaderSettings>['websiteHeader'];
  pagesRef: Record<string, PagesRefs>;
  hideNav?: boolean;
  welcomePhotoUrl?: string | null;
  hasUserInput: boolean;
  anchoringConfined: boolean;
  /** Slug the URL names while the stays redesign is active; undefined otherwise. */
  staysRedesignCurrentPageSlug?: string;
}>(({ filteredPages, hideNav, hasUserInput, anchoringConfined, staysRedesignCurrentPageSlug, ...restProps }) => {
  const { checkIsWelcomePage } = useWelcomePage();
  // Resolved here and passed down as a boolean — see `isRevealed` on Body's props for why.
  const shouldReveal = useShouldRevealPageContent();
  const deepLinkTargetSlug = useDeepLinkTargetSlug(restProps.eventHandle);
  // One page id, not a slug — see the same note in LayoutAloha: a mid-migration event has two
  // pages under the target slug, and decorating both gave anchoring two sentinels. PRNT-4799.
  const deepLinkTargetPageId = useMemo(
    () => (deepLinkTargetSlug ? filteredPages.find(p => isDeepLinkTargetSlug(p.pageSlug, deepLinkTargetSlug))?.id : undefined),
    [filteredPages, deepLinkTargetSlug],
  );
  // Sections above the target stay contained for the life of the page. Releasing on a readiness
  // signal was the bug — widgets keep growing long after their page query resolves (369px -> 3174px,
  // 40s later), and anchoring's exact compensation for that growth is itself scored. PRNT-4799.
  const isPreviewing = isInIframe();
  const targetIndex = filteredPages.findIndex(page => page.id === deepLinkTargetPageId);
  // Release phases: contained -> RELEASING (one forced-render commit, input-exempt and
  // compensated as a single swap) -> INTERACTIVE (auto, two frames later, once the swap has
  // painted). See DEFERRED_SECTION_STYLE_RELEASING.
  const [releaseSettled, setReleaseSettled] = useState(false);
  useEffect(() => {
    if (!hasUserInput || releaseSettled) return undefined;
    let innerId: number | null = null;
    const outerId = requestAnimationFrame(() => {
      innerId = requestAnimationFrame(() => setReleaseSettled(true));
    });
    return () => {
      cancelAnimationFrame(outerId);
      if (innerId !== null) cancelAnimationFrame(innerId);
    };
  }, [hasUserInput, releaseSettled]);
  const deferredStyle = !hasUserInput ? DEFERRED_SECTION_STYLE : releaseSettled ? DEFERRED_SECTION_STYLE_INTERACTIVE : DEFERRED_SECTION_STYLE_RELEASING;

  const isSectionDeferred = useCallback(
    (page: EventPageFragment, isWelcomePage: boolean, index: number): boolean => {
      // Root routes don't defer — see the same note in LayoutAloha: no target means no floor, and
      // the reveal would insert sections into view below a short welcome page. PRNT-4799.
      if (!deepLinkTargetPageId) return false;
      if (isPreviewing) return false;
      // Welcome is contained too when it sits above the target — its growth moved the target as much
      // as any other section's. Exempt on root routes only. PRNT-4799.
      if (isWelcomePage && targetIndex < 0) return false;
      // Never the landed-on section: the landing scroll measures it and the sentinel lives in it.
      if (page.id === deepLinkTargetPageId) return false;
      // Migrated (Custom) sections only; legacy sections are unchanged by this ticket.
      if (page.type !== EventPageType.custom) return false;
      // Only above the target — growth below it can't move the target.
      return targetIndex >= 0 && index < targetIndex;
    },
    [targetIndex, deepLinkTargetPageId, isPreviewing],
  );

  return (
    <>
      {filteredPages.map((page, index) => {
        const { isWelcomePage, isCustomWelcomePage } = checkIsWelcomePage(page.pageSlug, page.type);

        if (hideNav && isWelcomePage) {
          return null;
        }

        const isDeferred = isSectionDeferred(page, isWelcomePage, index);

        return (
          <PageSection
            key={page.id}
            page={page}
            isWelcomePage={isWelcomePage}
            isCustomWelcomePage={isCustomWelcomePage}
            isDeepLinkTarget={page.id === deepLinkTargetPageId}
            isDeferred={isDeferred}
            // Only the deferred sections read this, and it changes on each release phase. Passing it
            // to the rest handed their memo a changed prop they ignore, so every phase flip
            // re-rendered the whole layout instead of the contained sections.
            deferredStyle={isDeferred ? deferredStyle : undefined}
            isRevealed={shouldReveal({ type: page.type, pageSlug: page.pageSlug })}
            anchoringConfined={anchoringConfined}
            isStaysRedesignCurrentPage={staysRedesignCurrentPageSlug === page.pageSlug}
            {...restProps}
          />
        );
      })}
    </>
  );
});

const LayoutBrannanSinglePage: React.FC<LayoutBrannanProps> = ({ eventHandle, event, setLoadingTheme, setIsLastPageVisible, setIsPastFirstPage, hideNav }) => {
  const { info, pages, eventDesign } = event;
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobileOrTablet = useIsMobileOrTablet();
  const { getValueString } = useQueryParamHelper();
  const pagesRef = useMemo(() => ({}), []) as Record<string, PagesRefs>;

  const [isSm2orLarge] = useResponsive({ values: { _: false, sm2: true } });
  const [isGuestMobileFooterNavEnabled] = useIsFeatureEnabled('guestMobileFooterNavEnabled', { skip: isSm2orLarge });
  const hasUserInput = useHasUserInput();
  // Deliberately NOT hasUserInput itself: candidacy must stay confined through the release
  // swap (see ScrollableContainer). Relaxes once the release has settled.
  const [anchoringRelaxed, setAnchoringRelaxed] = useState(false);
  useEffect(() => {
    if (!hasUserInput || anchoringRelaxed) return undefined;
    const timer = setTimeout(() => setAnchoringRelaxed(true), RELEASE_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [hasUserInput, anchoringRelaxed]);

  // Drops migrated legacy pages, plus the RSVP page when it is effectively disabled or past its deadline
  const { pages: guestSitePages } = useGuestSitePages(pages, event.hasEnabledLegacyRsvpQuestions);

  const filteredPages = useMemo(() => guestSitePages.filter(page => !PAGES_TO_BE_SKIPPED.includes(page.type)), [guestSitePages]);
  const { checkIsWelcomePage } = useWelcomePage();
  const scrollPages = useMemo(() => {
    if (!hideNav) return filteredPages;
    return filteredPages.filter(page => !checkIsWelcomePage(page.pageSlug, page.type).isWelcomePage);
  }, [filteredPages, hideNav, checkIsWelcomePage]);

  useAlohaScroll(scrollPages, pagesRef, containerRef.current, setIsLastPageVisible, setIsPastFirstPage);
  const isPreviewing = isInIframe();
  // Admin preview (joy's own preview iframe) sets ?preview=true — distinct from other
  // iframe contexts like Carbon embedding or custom domains where the nav should remain sticky.
  const isAdminPreview = isPreviewing && getValueString('preview') === 'true';

  const { handleMenuClicked, handleFooterButtonClicked, handleEditEventClicked, handleMenuOpenedChange } = useLayoutBrannanController(guestSitePages);
  const { currentPageSlug, isCustomPageSlug, isUrlBasedCustomPageSlug } = useCurrentPageSlug(eventHandle);
  const isStaysRedesignActive = useIsStaysRedesignActive();
  const { isNativeAccommodationPage } = useAccommodationPageChecks(currentPageSlug);
  const { websiteHeader: savedWebsiteHeader } = useWebsiteHeaderSettings({
    eventHandle,
    targetWebsiteLayout: 'brannan',
  });
  // A Slider/Marquee header with a multi-photo Motion type on (Showcase, Polaroid) renders as the full-bleed header it plays on.
  const websiteHeader = useMultiPhotoMotionHeader(savedWebsiteHeader);
  const welcomePhotoUrl = useWelcomePagePhotoUrl({ event, headerPhoto: websiteHeader?.headerPhoto });

  // Accent art hugs the top of the page card, so drop the header's desktop top padding —
  // only when the in-flow header (the accent's host) actually renders.
  const masthead = useMasthead(eventDesign);
  const hasMasthead = !!masthead && !hideNav && !isNativeAccommodationPage && isMastheadCompatibleLayout(websiteHeader?.headerPhoto.presentationLayout);

  const { isSmallScreen, reapplyDesign, loading } = useBrannanDesign(eventHandle, eventDesign);
  // Stable value so the context doesn't re-render its consumers on every layout render.
  const brannanDesignContextValue = useMemo(() => ({ reapplyDesign }), [reapplyDesign]);
  const { pageColor, pageFontColor } = useColorPaletteProvider();

  // Create eventProps with filtered pages for navigation menus (including hamburger menu)
  const [isNewCustomArtPanelEnabled, isNewCustomArtFlagLoading] = useIsFeatureEnabled('newCustomArtPanelEnabled');
  const eventMenuProps = useMemo(() => {
    const menuProps = getEventMenuPropsFromEvent(event, isNewCustomArtPanelEnabled);
    // Banner held while the flag resolves, so a flagged event doesn't paint the legacy one and drop it.
    return { ...menuProps, isCustomArtEnabled: menuProps.isCustomArtEnabled && !isNewCustomArtFlagLoading, pages: guestSitePages };
  }, [event, isNewCustomArtPanelEnabled, isNewCustomArtFlagLoading, guestSitePages]);

  const headerProps = useMemo(() => {
    // Fallback to a type-agnostic lookup when the flag-based custom lookup misses a page
    // that hasn't been migrated yet. See LayoutBrannan.tsx for full explanation.
    const currentPage = currentPageSlug
      ? (findEventPageByPageSlug(guestSitePages, currentPageSlug, isCustomPageSlug, isUrlBasedCustomPageSlug) ??
        (isCustomPageSlug ? findEventPageByPageSlug(guestSitePages, currentPageSlug) : null))
      : null;

    return {
      page: currentPage?.type,
      pageSlug: currentPage?.pageSlug,
      pageId: currentPage?.id,
      isCustomPage: currentPage?.type === EventPageType.custom,
    };
  }, [currentPageSlug, isCustomPageSlug, isUrlBasedCustomPageSlug, guestSitePages]);

  const history = useHistory();
  const queryParams = useQueryParams();
  const eventPageRoute = useEventPageRoute(eventHandle, filteredPages);

  // CORE-6488: Legacy slug redirect for the Brannan single-page (scroll) layout.
  // Mirrors LayoutAloha — a URL with a pre-migration slug (e.g. /event/weddingparty) won't match
  // any rendered section, so redirect to the canonical URL and let the scroll logic find it.
  useEffect(() => {
    if (!currentPageSlug) return;
    const matchesFiltered = filteredPages.some(p => p.pageSlug.toLocaleLowerCase() === currentPageSlug.toLocaleLowerCase());
    if (matchesFiltered) return;
    const legacyPage = pages.find(p => p.pageSlug.toLocaleLowerCase() === currentPageSlug.toLocaleLowerCase());
    if (!legacyPage) return;
    const canonicalSlug = getMigratedPageSlug(legacyPage.type) ?? toCanonicalMigratedSlug(legacyPage.pageSlug);
    if (!canonicalSlug || canonicalSlug === currentPageSlug) return;
    const livePage = filteredPages.find(p => p.pageSlug === canonicalSlug || toCanonicalMigratedSlug(p.pageSlug) === canonicalSlug);
    const canonicalPath = livePage ? eventPageRoute.goToPath(livePage.type, livePage.pageSlug) : `/${eventHandle}/${canonicalSlug}`;
    if (canonicalPath && !canonicalPath.endsWith(`/${currentPageSlug}`)) {
      history.replace(
        queryString.stringifyUrl({
          url: canonicalPath,
          query: queryParams,
        }),
      );
    }
  }, [currentPageSlug, pages, filteredPages, eventHandle, eventPageRoute, history, queryParams]);

  useEffect(() => {
    setLoadingTheme?.(loading);
  }, [loading, setLoadingTheme]);

  useEffect(() => {
    if (isPreviewing) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [isPreviewing, headerProps.pageSlug]);

  return (
    <MobileTopBarNavOffsetProvider onMenuOpenChange={handleMenuOpenedChange}>
      <BrannanDesignProvider value={brannanDesignContextValue}>
        <ScrollableContainer
          ref={containerRef}
          id={SIMPLE_SCROLL_CONTAINER_ID}
          $anchoringConfined={!anchoringRelaxed}
        >
          {!hideNav && (
            <MobileTopBarNav
              eventHandle={eventHandle}
              // Brannan single-page strips `PAGES_TO_BE_SKIPPED` (moments, rsvp)
              // from the rendered page stack; the nav must use the same filtered set so it
              // never advertises a section the layout doesn't actually render.
              pages={filteredPages}
            />
          )}
          {!hideNav && !isSmallScreen && !isAdminPreview && (
            <StickyNavBar
              scrollableContainerRef={containerRef}
              pages={guestSitePages}
              eventHandle={eventHandle}
              handleMenuClicked={handleMenuClicked}
              eventProps={eventMenuProps}
              pageColor={pageColor}
              textColor={pageFontColor}
            />
          )}
          {websiteHeader && !hideNav && (
            <StandaloneWebsiteHeader
              websiteHeader={websiteHeader}
              navBarVariant={isSmallScreen ? 'menu' : 'inline'}
              pages={guestSitePages}
              currentPageType={EventPageType.welcome}
              eventHandle={eventHandle}
              handleMenuClicked={handleMenuClicked}
              eventProps={eventMenuProps}
              pageSlug={EVENT_PAGE_SLUGS.Welcome}
              eventDesign={eventDesign}
            />
          )}
          <Shell
            eventDesign={event.eventDesign}
            isCustomPage={headerProps.isCustomPage}
            styleApplicatorLoading={loading}
          >
            <PreloadImages
              pages={filteredPages}
              eventHandle={eventHandle}
              headerPhoto={websiteHeader?.headerPhoto}
              // Brannan's header renderers (PhotoBackground/Monogram, ArchPhoto, BrannanPhotoSlider,
              // PhotoTrio) all pass ssrSizes, so the eager sharp <img> is in the SSR markup and the
              // tiny placeholder's preload can be demoted. Aloha deliberately does not opt in —
              // AlohaPhotoSlider has no ssrSizes wiring yet.
              rendersEagerSsrHero
            />
            <Box paddingTop={isMobileOrTablet || hasMasthead ? 0 : '6rem'}>
              {!isNativeAccommodationPage && !hideNav && (
                <WebsiteHeader
                  data-testid={'layout-header'}
                  websiteHeader={websiteHeader}
                  applicatorProp={componentTitleBanner.prop}
                  navBarVariant={isSmallScreen ? 'menu' : 'inline'}
                  eventHandle={eventHandle}
                  pages={guestSitePages}
                  info={info}
                  handleMenuClicked={handleMenuClicked}
                  handleEditEventClicked={handleEditEventClicked}
                  eventProps={eventMenuProps}
                  page={headerProps.page}
                  pageSlug={headerProps.pageSlug}
                  pageId={headerProps.pageId}
                  eventDisplayName={info?.eventDisplayName}
                  masthead={masthead}
                />
              )}
            </Box>
            <BulkCustomPageDataProvider
              eventHandle={eventHandle}
              pages={filteredPages}
              eventPages={pages}
            >
              <PagesSection
                filteredPages={filteredPages}
                event={event}
                eventHandle={eventHandle}
                websiteHeader={websiteHeader}
                pagesRef={pagesRef}
                hideNav={hideNav}
                welcomePhotoUrl={welcomePhotoUrl}
                hasUserInput={hasUserInput}
                anchoringConfined={!anchoringRelaxed}
                staysRedesignCurrentPageSlug={isStaysRedesignActive ? currentPageSlug : undefined}
              />
            </BulkCustomPageDataProvider>
            {/* The footer must not be scroll-anchor eligible (it's content-stable while sections
                load, so Chrome preferred it over the target). ScrollableContainer's rule already
                excludes it — no per-element opt-out needed here. PRNT-4799. */}
            <Flex __css={brannanFooterContainerStyles}>
              {/* FooterNav is only visible on mobile */}
              {isGuestMobileFooterNavEnabled && !hideNav && (
                <Flex
                  width="100%"
                  display={{ _: 'flex', sm2: 'none' }}
                >
                  <FooterNav
                    pages={filteredPages}
                    eventHandle={eventHandle}
                    eventDisplayName={info?.eventDisplayName}
                    handleMenuClicked={handleMenuClicked}
                  />
                </Flex>
              )}
              <Footer
                onButtonClicked={handleFooterButtonClicked}
                data-testid={'layout-footer'}
              />
            </Flex>
          </Shell>
        </ScrollableContainer>
      </BrannanDesignProvider>
    </MobileTopBarNavOffsetProvider>
  );
};

LayoutBrannanSinglePage.displayName = 'LayoutBrannanSinglePage';

export { LayoutBrannanSinglePage };
